import { AuthShell } from './components/oktober/AuthShell';
import { moduleRoutes, standaloneModuleRoutes } from './lib/moduleRegistry';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RoleBasedRoute } from './components/RoleBasedRoute';
import { ADMIN_PAGE_ROLES } from './config/roles';
import { PageSkeleton } from './components/PageSkeleton';
import { OktoberLoadingScreen } from './components/oktober/OktoberLoadingScreen';

// Lazy load all pages and layout for code splitting
const AppLayout = lazy(() => import('./components/AppLayout').then((m) => ({ default: m.AppLayout })));
const Login = lazy(() => import('./pages/login/Login'));
const Register = lazy(() => import('./pages/register/Register'));
const VerifyEmail = lazy(() => import('./pages/verify-email/VerifyEmail'));
const ForgotPassword = lazy(() => import('./pages/forgot-password/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/reset-password/ResetPassword'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const Profile = lazy(() => import('./pages/profile/Profile'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const Counters = lazy(() => import('./pages/counters/Counters'));
const Users = lazy(() => import('./pages/users/Users'));
const Invitation = lazy(() => import('./pages/invitation/Invitation'));
const ActivityHistory = lazy(() => import('./pages/activity-history/ActivityHistory'));
const Pipeline = lazy(() => import('./pages/pipeline/Pipeline'));
const Calendar = lazy(() => import('./pages/calendar/Calendar'));
const MyWork = lazy(() => import('./pages/my-work/MyWork'));
const Task = lazy(() => import('./pages/task/Task'));
const Hr = lazy(() => import('./pages/hr/Hr'));
const Clients = lazy(() => import('./pages/clients/Clients'));
const ClientDetail = lazy(() => import('./pages/clients/ClientDetail'));
const Person = lazy(() => import('./pages/team/Person'));
const Review = lazy(() => import('./pages/review/Review'));
const SentEmails = lazy(() => import('./pages/review/SentEmails'));
const SentEmail = lazy(() => import('./pages/review/SentEmail'));
const Structure = lazy(() => import('./pages/structure/Structure'));
const Chats = lazy(() => import('./pages/chats/Chats'));
const AdminChats = lazy(() => import('./pages/admin-chats/AdminChats'));
const KlipDashboard = lazy(() => import('./pages/klip/KlipDashboard'));
const KlipCampaigns = lazy(() => import('./pages/klip/KlipCampaigns'));
const KlipMine = lazy(() => import('./pages/klip/KlipMine'));
const KlipAutomatik = lazy(() => import('./pages/klip/KlipAutomatik'));
const KlipFlow = lazy(() => import('./pages/klip/KlipFlow'));
const KlipAb = lazy(() => import('./pages/klip/KlipAb'));
const KlipCountdown = lazy(() => import('./pages/klip/KlipCountdown'));
const KlipCampaign = lazy(() => import('./pages/klip/KlipCampaign'));
const KlipNew = lazy(() => import('./pages/klip/KlipNew'));
const KlipReview = lazy(() => import('./pages/klip/KlipReview'));
const KlipAdmin = lazy(() => import('./pages/klip/KlipAdmin'));

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Suspense fallback={<OktoberLoadingScreen />}>
              <AppLayout sideBarCollapsible={true}/>
            </Suspense>
          </ProtectedRoute>
        }
      >
        {/* Upstream-only modules (src/modules/*), see lib/moduleRegistry.ts */}
        {moduleRoutes.map((route) => {
          const page = <Suspense fallback={<PageSkeleton />}>{route.element}</Suspense>;
          return (
            <Route
              key={route.path}
              path={route.path}
              element={route.roles ? <RoleBasedRoute allowedRoles={route.roles}>{page}</RoleBasedRoute> : page}
            />
          );
        })}
        <Route
          index
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Dashboard />
            </Suspense>
          }
        />
        <Route path="my-work" element={<Suspense fallback={<PageSkeleton />}><MyWork /></Suspense>} />
        {/* Klip: inside the full shell for admins, inside KlipShell for a Klip-only person */}
        <Route path="klip" element={<Suspense fallback={<PageSkeleton />}><KlipDashboard /></Suspense>} />
        <Route path="klip/mine" element={<Suspense fallback={<PageSkeleton />}><KlipMine /></Suspense>} />
        <Route path="klip/automatik" element={<Suspense fallback={<PageSkeleton />}><KlipAutomatik /></Suspense>} />
        <Route path="klip/flow" element={<Suspense fallback={<PageSkeleton />}><KlipFlow /></Suspense>} />
        <Route path="klip/ab" element={<Suspense fallback={<PageSkeleton />}><KlipAb /></Suspense>} />
        <Route path="klip/countdown" element={<Suspense fallback={<PageSkeleton />}><KlipCountdown /></Suspense>} />
        <Route path="klip/campaigns" element={<Suspense fallback={<PageSkeleton />}><KlipCampaigns /></Suspense>} />
        <Route path="klip/campaigns/:id" element={<Suspense fallback={<PageSkeleton />}><KlipCampaign /></Suspense>} />
        <Route path="klip/new/:clickupTaskId" element={<Suspense fallback={<PageSkeleton />}><KlipNew /></Suspense>} />
        <Route path="klip/klips/:klipId/review" element={<Suspense fallback={<PageSkeleton />}><KlipReview /></Suspense>} />
        <Route
          path="klip/admin"
          element={<RoleBasedRoute allowedRoles={[...ADMIN_PAGE_ROLES]}><Suspense fallback={<PageSkeleton />}><KlipAdmin /></Suspense></RoleBasedRoute>}
        />
        <Route path="tasks/:id" element={<Suspense fallback={<PageSkeleton />}><Task /></Suspense>} />
        <Route path="tasks" element={<Suspense fallback={<PageSkeleton />}><Pipeline /></Suspense>} />
        <Route path="calendar" element={<Suspense fallback={<PageSkeleton />}><Calendar /></Suspense>} />
        <Route path="hr" element={<Suspense fallback={<PageSkeleton />}><Hr /></Suspense>} />
        <Route path="clients" element={<Suspense fallback={<PageSkeleton />}><Clients /></Suspense>} />
        <Route path="clients/:slug" element={<Suspense fallback={<PageSkeleton />}><ClientDetail /></Suspense>} />
        {/* The old Team list: the org chart shows everyone now */}
        <Route path="team" element={<Navigate to="/hr?tab=org" replace />} />
        <Route path="team/:id" element={<Suspense fallback={<PageSkeleton />}><Person /></Suspense>} />
        <Route path="review/:id" element={<Suspense fallback={<PageSkeleton />}><Review /></Suspense>} />
        <Route path="sent" element={<Suspense fallback={<PageSkeleton />}><SentEmails /></Suspense>} />
        <Route path="sent/:id" element={<Suspense fallback={<PageSkeleton />}><SentEmail /></Suspense>} />
        <Route path="structure" element={<Suspense fallback={<PageSkeleton />}><Structure /></Suspense>} />
        <Route path="chats" element={<Suspense fallback={<PageSkeleton />}><Chats /></Suspense>} />
        <Route path="chats/:id" element={<Suspense fallback={<PageSkeleton />}><Chats /></Suspense>} />
        {/* Superadmins only: the page sends anyone else home */}
        <Route path="admin-chats" element={<Suspense fallback={<PageSkeleton />}><AdminChats /></Suspense>} />
        <Route path="admin-chats/:id" element={<Suspense fallback={<PageSkeleton />}><AdminChats /></Suspense>} />
        <Route
          path="profile"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Profile />
            </Suspense>
          }
        />
        <Route
          path="settings"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Settings />
            </Suspense>
          }
        />
        <Route
          path="counters"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Counters />
            </Suspense>
          }
        />
        <Route
          path="users"
          element={
            <RoleBasedRoute allowedRoles={[...ADMIN_PAGE_ROLES]}>
              <Suspense fallback={<PageSkeleton />}>
                <Users />
              </Suspense>
            </RoleBasedRoute>
          }
        />
        <Route
          path="activity-history"
          element={
            <RoleBasedRoute allowedRoles={[...ADMIN_PAGE_ROLES]}>
              <Suspense fallback={<PageSkeleton />}>
                <ActivityHistory />
              </Suspense>
            </RoleBasedRoute>
          }
        />
      </Route>
      {/* Upstream-only module pages with a layout of their own */}
      {standaloneModuleRoutes.map((route) => {
        const page = <Suspense fallback={<PageSkeleton minimal />}>{route.element}</Suspense>;
        return (
          <Route
            key={route.path}
            path={`/${route.path}`}
            element={
              <ProtectedRoute>
                {route.roles ? <RoleBasedRoute allowedRoles={route.roles}>{page}</RoleBasedRoute> : page}
              </ProtectedRoute>
            }
          />
        );
      })}
      <Route
        path="/login"
        element={
          <AuthShell>
            <Suspense fallback={<PageSkeleton minimal />}>
              <Login />
            </Suspense>
          </AuthShell>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <AuthShell>
            <Suspense fallback={<PageSkeleton minimal />}>
              <ForgotPassword />
            </Suspense>
          </AuthShell>
        }
      />
      <Route
        path="/reset-password/:token"
        element={
          <AuthShell>
            <Suspense fallback={<PageSkeleton minimal />}>
              <ResetPassword />
            </Suspense>
          </AuthShell>
        }
      />
      <Route
        path="/register"
        element={
          <AuthShell>
            <Suspense fallback={<PageSkeleton minimal />}>
              <Register />
            </Suspense>
          </AuthShell>
        }
      />
      <Route
        path="/verify-email/:token"
        element={
          <AuthShell>
            <Suspense fallback={<PageSkeleton minimal />}>
              <VerifyEmail />
            </Suspense>
          </AuthShell>
        }
      />
      <Route
        path="/token/:token"
        element={
          <AuthShell>
            <Suspense fallback={<PageSkeleton minimal />}>
              <Invitation />
            </Suspense>
          </AuthShell>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
